import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useBranding } from "../../contexts/BrandingContext";
import { Scissors, Instagram, MapPin, Phone, Mail, Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import { useState } from "react";

const NAV = [
  { to: "/salon", label: "Home", end: true },
  { to: "/salon/services", label: "Services" },
  { to: "/salon/team", label: "Our Team" },
  { to: "/salon/book", label: "Book" },
];

export default function ClientSiteLayout() {
  const branding = useBranding();
  const logoSrc = branding.getLogoSrc?.();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="client-site-layout">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/salon" className="flex items-center gap-2.5" data-testid="client-site-logo">
            {logoSrc ? (
              <img src={logoSrc} alt={branding.app_name} className="h-8 w-auto" />
            ) : (
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center text-white shadow-sm">
                <Scissors className="w-4 h-4" strokeWidth={2} />
              </div>
            )}
            <span className="font-['Outfit'] text-[17px] font-semibold tracking-tight text-slate-900">
              {branding.app_name || "Salon"}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium font-['Manrope'] transition-all ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
                data-testid={`client-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                {item.label}
              </NavLink>
            ))}
            <Link to="/salon/book" className="ml-3">
              <Button className="h-9 bg-gradient-to-r from-rose-500 to-violet-500 hover:from-rose-600 hover:to-violet-600 text-white rounded-full px-5 text-sm font-semibold shadow-sm border-0" data-testid="client-nav-book-now">
                Book Now
              </Button>
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button onClick={() => setOpen(v => !v)} className="md:hidden p-2 text-slate-600" data-testid="client-mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t border-slate-100 bg-white" data-testid="client-mobile-menu">
            <div className="px-5 py-3 space-y-1">
              {NAV.map(item => (
                <NavLink key={item.to} to={item.to} end={item.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2.5 rounded-lg text-sm font-medium font-['Manrope'] ${
                      isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }>
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <main key={location.pathname} className="flex-1 animate-fadeIn">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-slate-100 bg-slate-50/50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              {logoSrc ? (
                <img src={logoSrc} alt={branding.app_name} className="h-7 w-auto" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center text-white">
                  <Scissors className="w-3.5 h-3.5" />
                </div>
              )}
              <span className="font-['Outfit'] text-base font-semibold text-slate-900">
                {branding.app_name || "Salon"}
              </span>
            </div>
            <p className="text-sm text-slate-500 font-['Manrope'] leading-relaxed max-w-sm">
              Premium hair care, skin, and beauty services delivered by our passionate team. Book online in under 2 minutes.
            </p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope']">Explore</p>
            <div className="space-y-2">
              {NAV.map(item => (
                <Link key={item.to} to={item.to} className="block text-sm text-slate-600 hover:text-slate-900 font-['Manrope']">
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3 font-['Manrope']">Access</p>
            <div className="space-y-2">
              <Link to="/login" className="block text-sm text-slate-600 hover:text-slate-900 font-['Manrope']" data-testid="footer-staff-login">Staff Login</Link>
              <Link to="/employee/login" className="block text-sm text-slate-600 hover:text-slate-900 font-['Manrope']">Employee Portal</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-xs text-slate-400 font-['Manrope']">© {new Date().getFullYear()} {branding.app_name || "Salon"}. All rights reserved.</p>
            <p className="text-xs text-slate-400 font-['Manrope']">Powered by SalonFlow OS</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
